import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Stack, XStack, useTheme, styled } from 'tamagui';
import { setSkillSettings } from 'reducers/slices/skillsettings';
import { ColorBlock } from 'components/utility/Buttons';
import { useSelector, useDispatch } from 'react-redux';
import { ColorButton } from 'components/utility/Buttons';
import Modal from 'components/utility/Modal';
import { toggleLevels } from 'reducers/slices/hskstatus';
import { ShadowCard } from 'components/base/General';
import { useGetSkillLevelsQuery } from 'utils/RTKGraphQL';
import Loading from 'components/utility/Loading';
import ChildPage from '../main/ChildPage';

const colorMapper: { [key: string]: string } = {
  hsk1: 'levelOne',
  hsk2: 'levelTwo',
  hsk3: 'levelThree',
  hsk4: 'levelFour',
  hsk5: 'levelFive',
  hsk6: 'levelSix',
  hsk7: 'levelSeven',
  hsk8: 'levelEight',
  hsk9: 'levelNine',
  'hsk9+': 'levelTen',
};

export const HSKModal = ({ hskstatus }: { hskstatus: boolean }) => {
  const theme = useTheme();
  const settings = useSelector((state: any) => state.skillsettings);
  const dispatch = useDispatch();
  const { data: skillLevels, isLoading, error } = useGetSkillLevelsQuery();

  const [selectedSkills, setSelectedSkills] = useState<Set<string>>(
    new Set(settings.skill?.map((skill: any) => skill.value) || [])
  );

  const filteredSkillLevels = useMemo(
    () => skillLevels?.filter((skill: any) => skill.value !== 'hsk9plus') || [],
    [skillLevels]
  );

  const colorGen = useCallback(
    (value: string) =>
      selectedSkills.has(value) ? colorMapper[value] : 'gray',
    [selectedSkills]
  );

  const switchLevel = useCallback((level: string) => {
    setSelectedSkills((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(level)) {
        newSet.delete(level);
      } else {
        newSet.add(level);
      }
      return newSet;
    });
  }, []);

  const saveLevel = () => {
    const newSkills = filteredSkillLevels.filter((skill: any) =>
      selectedSkills.has(skill.value)
    );
    dispatch(setSkillSettings({ skill: newSkills }));
    dispatch(toggleLevels(false));
  };

  if (isLoading)
    return (
      <ChildPage>
        <Loading />
      </ChildPage>
    );
  if (error) return <Stack>Error: {error.message}</Stack>;

  return (
    <Modal
      backdropOpacity={0}
      isVisible={hskstatus}
      onBackdropPress={() => dispatch(toggleLevels(false))}
      animationIn="fadeInUp"
      animationOut="fadeOutDown"
    >
      <ShadowCard
        width={350}
        left="0%"
        $sm={{ width: 450, left: '10%' }}
        $md={{ width: 500, left: '12%' }}
        $lg={{ left: '20%', top: 25 }}
        $xl={{ left: '30%' }}
        height={275}
        bg={theme.cardBG}
        pt={20}
        pb={8}
        borderRadius={15}
        shadow={5}
        justifyContent="space-between"
        alignItems="center"
        display="flex"
      >
        <XStack
          flexWrap="wrap"
          justifyContent="center"
          maxWidth={500}
          zIndex={9999}
        >
          {filteredSkillLevels.map((item: any) => (
            <ColorBlock
              colorVal={colorGen(item.value)}
              key={item.value}
              onPress={() => switchLevel(item.value)}
            >
              {item.label}
            </ColorBlock>
          ))}
        </XStack>
        <ColorButton
          title="Save"
          onPress={saveLevel}
          width={75}
          height={35}
          fontSize="$xs"
          alignItems="center"
        />
      </ShadowCard>
    </Modal>
  );
};
